import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationsModal = ({ isOpen, onClose }) => {
  const [filter, setFilter] = useState('all'); // all, announcements, updates, alerts

  const notifications = [
    {
      id: 1,
      type: 'announcement',
      icon: 'TrendingUp',
      iconColor: 'text-success',
      iconBg: 'bg-success/10',
      title: 'New Promotional Rates Announced',
      message: 'New promotional rates effective December 1st - up to 0.5% bonus commission on all transactions over $1,000. Take advantage of this limited-time offer to maximize your earnings.',
      date: '2025-11-18',
      time: '10:30 AM',
      isRead: false,
      priority: 'high'
    },
    {
      id: 2,
      type: 'update',
      icon: 'Users',
      iconColor: 'text-primary',
      iconBg: 'bg-primary/10',
      title: 'Agent Training Webinar',
      message: 'Monthly agent training webinar scheduled for November 25th at 2:00 PM EST. Topics include new compliance requirements, customer service best practices, and Q4 performance strategies.',
      date: '2025-11-17',
      time: '3:45 PM',
      isRead: false,
      priority: 'medium'
    },
    {
      id: 3,
      type: 'alert',
      icon: 'Award',
      iconColor: 'text-warning',
      iconBg: 'bg-warning/10',
      title: 'Q4 Performance Bonuses Available',
      message: 'Q4 performance bonuses now available - check your commission tracker to see your eligible bonus amount. Bonuses range from 2-8% based on quarterly performance metrics.',
      date: '2025-11-16',
      time: '11:20 AM',
      isRead: true,
      priority: 'high'
    },
    {
      id: 4,
      type: 'announcement',
      icon: 'ShieldCheck',
      iconColor: 'text-info',
      iconBg: 'bg-info/10',
      title: 'Enhanced Security Features',
      message: 'We have implemented new two-factor authentication options for enhanced account security. Update your security settings to enable these features.',
      date: '2025-11-15',
      time: '9:15 AM',
      isRead: true,
      priority: 'medium'
    },
    {
      id: 5,
      type: 'update',
      icon: 'FileText',
      iconColor: 'text-accent',
      iconBg: 'bg-accent/10',
      title: 'Updated Compliance Documentation',
      message: 'New AML/KYC compliance documentation requirements are now in effect. Review the updated guidelines in your agent portal to ensure full compliance.',
      date: '2025-11-14',
      time: '2:00 PM',
      isRead: true,
      priority: 'high'
    },
    {
      id: 6,
      type: 'alert',
      icon: 'DollarSign',
      iconColor: 'text-success',
      iconBg: 'bg-success/10',
      title: 'Special Holiday Promotion',
      message: 'Holiday season special: Earn double commission on first-time customer referrals from December 1-31. Help new customers send money home for the holidays.',
      date: '2025-11-13',
      time: '10:00 AM',
      isRead: true,
      priority: 'medium'
    },
    {
      id: 7,
      type: 'announcement',
      icon: 'Globe',
      iconColor: 'text-primary',
      iconBg: 'bg-primary/10',
      title: 'New Corridor Added: USA to Nigeria',
      message: 'We are excited to announce a new money transfer corridor from USA to Nigeria with competitive rates. Start serving customers in this high-demand corridor today.',
      date: '2025-11-12',
      time: '1:30 PM',
      isRead: true,
      priority: 'medium'
    }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Notifications', count: notifications?.length },
    { value: 'announcements', label: 'Announcements', count: notifications?.filter(n => n?.type === 'announcement')?.length },
    { value: 'updates', label: 'Updates', count: notifications?.filter(n => n?.type === 'update')?.length },
    { value: 'alerts', label: 'Alerts', count: notifications?.filter(n => n?.type === 'alert')?.length }
  ];

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications?.filter(n => n?.type === filter?.toLowerCase()?.slice(0, -1));

  const unreadCount = notifications?.filter(n => !n?.isRead)?.length;

  const handleMarkAllRead = () => {
    console.log('Mark all as read');
  };

  const handleMarkAsRead = (id) => {
    console.log('Mark notification as read:', id);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Bell" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Agent Notifications</h2>
              <p className="text-sm text-muted-foreground">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted/30">
          <div className="flex space-x-2">
            {filterOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => setFilter(option?.value)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${filter === option?.value
                    ? 'bg-primary text-white' :'bg-background text-foreground hover:bg-muted'
                  }
                `}
              >
                {option?.label}
                <span className={`
                  ml-2 px-2 py-0.5 rounded-full text-xs
                  ${filter === option?.value
                    ? 'bg-white/20' :'bg-muted'
                  }
                `}>
                  {option?.count}
                </span>
              </button>
            ))}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              iconName="CheckCheck"
              iconPosition="left"
            >
              Mark all as read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredNotifications?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Bell" size={32} className="text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">No notifications found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications?.map((notification) => (
                <div
                  key={notification?.id}
                  className={`
                    bg-background rounded-lg border p-4 transition-all hover:shadow-md
                    ${!notification?.isRead ? 'border-primary/50 bg-primary/5' : 'border-border'}
                  `}
                >
                  <div className="flex items-start space-x-4">
                    {/* Icon */}
                    <div className={`
                      w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                      ${notification?.iconBg}
                    `}>
                      <Icon name={notification?.icon} size={20} className={notification?.iconColor} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-foreground">
                            {notification?.title}
                          </h3>
                          {!notification?.isRead && (
                            <span className="w-2 h-2 bg-primary rounded-full"></span>
                          )}
                          {notification?.priority === 'high' && (
                            <span className="px-2 py-0.5 bg-error/10 text-error text-xs rounded-full">
                              Important
                            </span>
                          )}
                        </div>
                        {!notification?.isRead && (
                          <button
                            onClick={() => handleMarkAsRead(notification?.id)}
                            className="text-xs text-primary hover:underline ml-2 flex-shrink-0"
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {notification?.message}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} />
                          <span>{new Date(notification?.date)?.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Clock" size={12} />
                          <span>{notification?.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {filteredNotifications?.length} of {notifications?.length} notification{notifications?.length !== 1 ? 's' : ''}
            </p>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;